import { Suspense, lazy, useCallback, useEffect } from 'react'
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import Loading from 'modules/core/components/Loading'
import { useNavigation } from 'modules/core/hooks/useNavigation'

import { InsightTableType } from 'modules/finance/components/FinancialWorkspace/types/table'

// Lazy load the heavy payroll component to reduce initial bundle size
// This defers loading until the modal is actually opened
const ImportPayrollResultModal = lazy(
  () => import('modules/compensations/components/ImportPayrollResultModal')
)

const ImportPayrollResultModalPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { navigation } = useNavigation()
  const { canSeeFinanceWorkspace } = useOutletContext<{
    canSeeFinanceWorkspace: boolean
  }>()
  const params = new URLSearchParams(location.search)
  const source = params.get('source')

  const onClose = useCallback(() => {
    // Ideally, navigate(-1) should work, but it doesn't
    const routes = [
      {
        condition: source === InsightTableType.EmployeeCosts,
        path: navigation.finance.financialWorkspace.employee_costs.fullPath(),
      },
      {
        condition: source === InsightTableType.Payroll,
        path: navigation.finance.financialWorkspace.payroll.fullPath(),
      },
      {
        condition: source === InsightTableType.Compensation,
        path: navigation.finance.financialWorkspace.compensations.fullPath(),
      },
    ]

    const route = routes.find((r) => r.condition)
    // Navigate to matched route or fallback to main financial workspace
    navigate(route?.path ?? navigation.finance.financialWorkspace.fullPath())
  }, [navigate, navigation, source])

  // Redirect unauthorized users and prevent render
  // Using useEffect to handle navigation side effect properly
  useEffect(() => {
    if (!canSeeFinanceWorkspace) {
      onClose()
    }
  }, [canSeeFinanceWorkspace, onClose])

  // Early return to prevent lazy loading while redirecting
  if (!canSeeFinanceWorkspace) {
    return null
  }

  return (
    <ErrorBoundary blankSlate>
      <Suspense fallback={<Loading />}>
        <ImportPayrollResultModal onClose={onClose} onFinish={onClose} />
      </Suspense>
    </ErrorBoundary>
  )
}

export default ImportPayrollResultModalPage
